/**
 * TanStack Query hooks for the Dynamics 365 F&O front-office board.
 *
 * **The poll interval comes from the payload, not from a constant here.** Every
 * `/fo/*` response carries `cache_ttl_sec` — how long the backend reuses a
 * snapshot before it re-reads Log Analytics — and the four routes deliberately
 * disagree: the overview rebuilds every 2 min, integration every 5, requests
 * every 10, user experience every 15 (`nova.fo.service`, all env-overridable).
 * Polling faster than the backend rebuilds just re-serves identical bytes, which
 * is the reasoning the other hook files state as a hardcoded number; here the
 * number is on the wire, so it is read from there instead. A hardcoded interval
 * would also silently go wrong the moment someone tunes `NOVA_FO_*_TTL_SEC`.
 *
 * Until the first response lands there is nothing to read the TTL from, so the
 * fallback below is used for exactly one interval.
 *
 * None of these routes fails because Log Analytics is unreadable: a missing
 * credential, a 403, a 404 and a rejected query are all fields on a 200
 * (`configured` / `access_state` / `status_detail` / `errors`). A thrown error
 * here therefore means the *NOVA* API is down, not Azure — worth remembering
 * when reading the `error` branch. As of 2026-08-20 both workspaces are
 * unreadable, so `access_state !== "ok"` is the expected shape of a successful
 * response, not a bug.
 *
 * SDK functions are imported from `@/lib/api/generated` rather than the
 * `@/lib/api` barrel, matching the other per-domain hook files: the barrel
 * re-exports these hooks, so importing it here would close an import cycle.
 */

import { useQuery } from "@tanstack/react-query";

import {
  getFoIntegration,
  getFoOverview,
  getFoRequests,
  getFoUserExperience,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";

/** Query key for the board's headline payload (KPIs, availability, environments). */
export const FO_OVERVIEW_KEY = ["fo", "overview"] as const;
/** Query key for form performance. */
export const FO_USER_EXPERIENCE_KEY = ["fo", "user-experience"] as const;
/** Query key for request health. */
export const FO_REQUESTS_KEY = ["fo", "requests"] as const;
/** Query key for Service Bus queue health (the second workspace). */
export const FO_INTEGRATION_KEY = ["fo", "integration"] as const;

/**
 * Interval used only before the first response arrives, in ms.
 *
 * The shortest TTL any of the four routes ships with, so the first refetch can
 * never be slower than the backend's own fastest rebuild. After that the
 * payload's own `cache_ttl_sec` takes over.
 */
const FALLBACK_INTERVAL_MS = 120_000;

/**
 * Refetch on the cadence the payload itself declares.
 *
 * Called with the last successful response, so `cache_ttl_sec` sets the next
 * interval — the client tracks the backend's TTL automatically instead of
 * guessing at it. Takes the *data* rather than the query so it cannot feed back
 * into TanStack's inference of the query's own data type.
 */
function intervalFromTtl(data: { cache_ttl_sec: number } | undefined): number {
  const ttlSec = data?.cache_ttl_sec;
  return ttlSec != null && ttlSec > 0 ? ttlSec * 1000 : FALLBACK_INTERVAL_MS;
}

/** The board's entry query: KPIs with their thresholds, availability trend, environments. */
export function useFoOverview() {
  return useQuery({
    queryKey: FO_OVERVIEW_KEY,
    queryFn: async () => {
      const { data } = await getFoOverview();
      logger.debug("[fo] overview received", {
        accessState: data?.access_state,
        workspace: data?.workspace,
        environments: data?.environments.length ?? 0,
        trendPoints: data?.availability_trend.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
  });
}

/**
 * Form performance: worst forms by P95, hourly P95 trend, duration histogram,
 * load by hour of day.
 *
 * @param enabled Skip the request while the board's entry query reports no
 *   credential at all — the answer is a known-empty payload for the same
 *   reason, and the board is already explaining it.
 */
export function useFoUserExperience(enabled = true) {
  return useQuery({
    queryKey: FO_USER_EXPERIENCE_KEY,
    queryFn: async () => {
      const { data } = await getFoUserExperience();
      logger.debug("[fo] user experience received", {
        accessState: data?.access_state,
        forms: data?.worst_forms.length ?? 0,
        foldedCohorts: data?.suppression.cohorts_folded,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
    enabled,
  });
}

/** Request health: result codes, failing dependencies, the app-vs-SQL split. */
export function useFoRequests(enabled = true) {
  return useQuery({
    queryKey: FO_REQUESTS_KEY,
    queryFn: async () => {
      const { data } = await getFoRequests();
      logger.debug("[fo] requests received", {
        accessState: data?.access_state,
        resultCodes: data?.result_codes.length ?? 0,
        dependencies: data?.failed_dependencies.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
    enabled,
  });
}

/**
 * Service Bus queue health — from a *different* workspace in a different
 * subscription, so it reports its own access state independently of the other
 * three and is never gated on theirs.
 */
export function useFoIntegration() {
  return useQuery({
    queryKey: FO_INTEGRATION_KEY,
    queryFn: async () => {
      const { data } = await getFoIntegration();
      logger.debug("[fo] integration received", {
        accessState: data?.access_state,
        workspaceResourceId: data?.workspace_resource_id,
        queues: data?.queues.length ?? 0,
      });
      return data;
    },
    refetchInterval: (query) => intervalFromTtl(query.state.data),
  });
}
